import { Container, Sprite, Assets } from 'pixi.js';

/**
 * Privacy-safe visual-only deck pile, shared by OnlineTableRenderer.js
 * (Ronda) and OnlineBacigaRenderer.js (Báciga) — a sibling of the offline
 * src/game/DeckRenderer.js, but deliberately NOT given a real Deck instance:
 * it only ever knows how many cards are left in the stock (a plain count
 * the server already reveals via PlayerView.deckRemaining/
 * BacigaPlayerView.deckRemaining, same privacy tier as otherHandCounts),
 * never their identity. Every sprite is a plain card-back image with no
 * card data attached at all — nothing here could leak deck order even if a
 * client inspected its own memory (RONDA_ONLINE.md's standing principle:
 * the server is the only place real card data ever lives).
 */
class OnlineDeckRenderer {
    constructor(app) {
        this.app = app;
        this.container = null;
        this.cardSprites = [];
        this.cardWidth = 100;
        this.cardHeight = 150;
        this.stackOffsetX = 0.2;
        this.stackOffsetY = 0.2;
        this.x = 0;
        this.y = 0;
        this.rotation = 0;
    }

    getCardBackPath() {
        const cardBack = localStorage.getItem('cardBack') || '001';
        return `assets/images/deck/${cardBack}.png`;
    }

    /** Rebuilds the stack from scratch with exactly `count` face-down cards. */
    async render(count) {
        if (this.container) {
            this.container.destroy({ children: true });
            this.container = null;
            this.cardSprites = [];
        }
        if (count <= 0) return;

        this.container = new Container();
        this.container.label = 'OnlineDeckContainer';
        this.container.x = this.x;
        this.container.y = this.y;
        this.container.rotation = this.rotation;
        this.container.zIndex = 50;
        this.app.stage.addChild(this.container);

        const texture = await Assets.load(this.getCardBackPath());
        for (let i = 0; i < count; i++) {
            const sprite = new Sprite(texture);
            sprite.width = this.cardWidth;
            sprite.height = this.cardHeight;
            sprite.anchor.set(0.5);
            sprite.x = i * this.stackOffsetX;
            sprite.y = i * this.stackOffsetY;
            this.container.addChild(sprite);
            this.cardSprites.push(sprite);
        }
    }

    /** Adjusts the visible stack to `count` cards — a shrink just fades/removes the top sprites; a growth (a fresh shuffled deck after a round finishes) rebuilds from scratch since there's nothing to grow from. */
    async setCount(count) {
        if (count === this.cardSprites.length) return;
        if (!this.container || count > this.cardSprites.length) {
            await this.render(count);
            return;
        }
        const toRemove = this.cardSprites.splice(count);
        toRemove.forEach((sprite) => {
            if (sprite.parent) sprite.parent.removeChild(sprite);
            sprite.destroy();
        });
    }

    setPosition(x, y, rotation = 0) {
        this.x = x;
        this.y = y;
        this.rotation = rotation;
        if (this.container) {
            this.container.x = x;
            this.container.y = y;
            this.container.rotation = rotation;
        }
    }

    /** Current pile position/rotation — used as the flight origin for cards animating out of the deck during a deal. */
    getPosition() {
        return { x: this.x, y: this.y, rotation: this.rotation };
    }

    /** Instantly pops and destroys the top-most sprite (no fade) — mirrors offline DeckRenderer.removeCard(idx, animate=false), called once per card as a deal animation pulls it off the pile. */
    removeTopCard() {
        const sprite = this.cardSprites.pop();
        if (!sprite) return;
        if (this.container && sprite.parent === this.container) {
            this.container.removeChild(sprite);
        }
        if (!sprite.destroyed) sprite.destroy();
    }

    /**
     * Same per-slot corner placement offline's DeckRenderer.setPositionByPlayer
     * uses (bottom-right for slot 0, rotating around the table) — `visualSlot`
     * here is already rotated so "me" is always 0, same convention as every
     * other online position helper (see onlineSeat.js).
     */
    setPositionByVisualSlot(visualSlot, screenWidth, screenHeight) {
        const margin = 20;
        switch (visualSlot) {
            case 0:
                this.setPosition(screenWidth - margin - this.cardWidth / 2, screenHeight - margin * 2 - this.cardHeight, 0);
                break;
            case 1:
                this.setPosition(screenWidth - margin - this.cardHeight / 2, screenHeight - margin * 2 - this.cardHeight, Math.PI / 2);
                break;
            case 2:
                this.setPosition(margin + this.cardWidth / 2, margin + this.cardHeight / 2, Math.PI);
                break;
            case 3:
                this.setPosition(margin + this.cardHeight / 2, margin * 2, -Math.PI / 2);
                break;
            default:
                this.setPosition(screenWidth / 2, screenHeight / 2, 0);
        }
    }

    setCardSize(width, height) {
        this.cardWidth = width;
        this.cardHeight = height;
        this.cardSprites.forEach((sprite) => {
            sprite.width = width;
            sprite.height = height;
        });
    }

    destroy() {
        if (this.container) {
            this.cardSprites.forEach((sprite) => sprite.destroy());
            this.cardSprites = [];
            if (this.container.parent) this.container.parent.removeChild(this.container);
            this.container.destroy();
            this.container = null;
        }
    }
}

export default OnlineDeckRenderer;
